import type { TimeEntriesExpanded } from '@/types/timeTracking'
import {
    breakOverlapSeconds,
    formatTimeOfDay,
    getISODate,
    type Interval,
    secondsToHours,
    workIntervalsByEntry,
} from './dates'
import { displayLocationName } from './location-utils'

function escapeCSVField(value: string | undefined | null): string {
    if (value == null) return ''
    const str = String(value)
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`
    }
    return str
}

export const CSV_HEADERS_WITH_USER = [
    'Date',
    'User Name',
    'Job Name',
    'Tags',
    'Location Name',
    'Clock In',
    'Clock Out',
    'Hours',
    'Status',
    'Entry Type',
    'Notes',
]

const CSV_HEADERS_WITHOUT_USER = [
    'Date',
    'Job Name',
    'Tags',
    'Location Name',
    'Clock In',
    'Clock Out',
    'Hours',
    'Status',
    'Entry Type',
    'Notes',
]

export type TagNameLookup = (tagIds: string[] | undefined) => string

interface GenerateCSVOptions {
    includeUser?: boolean
    resolveTagNames?: TagNameLookup
}

function str(value: string | undefined | null): string {
    return value == null ? '' : String(value)
}

// Net seconds a single row contributes to its day total. Work entries
// contribute their full duration; a break contributes the negative of only the
// portion overlapping work — so the rows reconcile with the overlap-aware day
// total (a break taken between jobs, with no overlap, is 0).
function rowNetSeconds(entry: TimeEntriesExpanded, workIntervals: Interval[]): number {
    if (entry.entry_type === 'break') {
        return -breakOverlapSeconds(entry, workIntervals)
    }
    return entry.duration || 0
}

export function buildEntryRow(
    entry: TimeEntriesExpanded,
    includeUser: boolean,
    resolveTagNames?: TagNameLookup,
    workIntervals: Interval[] = []
): string[] {
    const dateStr = entry.clock_in ? getISODate(entry.clock_in) : ''
    const clockInTime = entry.clock_in ? formatTimeOfDay(entry.clock_in) : ''
    const clockOutTime = entry.clock_out ? formatTimeOfDay(entry.clock_out) : ''
    const hours = secondsToHours(rowNetSeconds(entry, workIntervals)).toFixed(2)
    const tagsStr = resolveTagNames ? resolveTagNames(entry.expand?.job?.tags) : ''

    const commonFields = [
        str(entry.expand?.job?.name),
        tagsStr,
        str(displayLocationName(entry.expand?.location?.name)),
        clockInTime,
        clockOutTime,
        hours,
        str(entry.status),
        str(entry.entry_type),
        str(entry.notes),
    ]

    if (includeUser) {
        return [dateStr, str(entry.expand?.user?.name), ...commonFields]
    }
    return [dateStr, ...commonFields]
}

/**
 * Builds a row per entry, netting each break against only its own user's
 * same-day work — matching the approval screen's per-user/day totals. A single
 * flat interval set would leak break overlap across users and days.
 */
export function buildEntryRows(
    entries: TimeEntriesExpanded[],
    includeUser: boolean,
    resolveTagNames?: TagNameLookup
): string[][] {
    const intervalsByEntry = workIntervalsByEntry(entries)
    return entries.map(entry =>
        buildEntryRow(entry, includeUser, resolveTagNames, intervalsByEntry.get(entry.id) ?? [])
    )
}

export function generateTimeEntriesCSV(
    entries: TimeEntriesExpanded[],
    options: GenerateCSVOptions = {}
): string {
    const { includeUser = true, resolveTagNames } = options
    const headers = includeUser ? CSV_HEADERS_WITH_USER : CSV_HEADERS_WITHOUT_USER
    const rows: string[] = [headers.map(escapeCSVField).join(',')]

    for (const row of buildEntryRows(entries, includeUser, resolveTagNames)) {
        rows.push(row.map(escapeCSVField).join(','))
    }

    return rows.join('\n')
}
